// skipcq: JS-W1028
import React, { useCallback, useEffect, useState } from 'react';

import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';
// Context
import {
  AuthSessionSyncContext,
  defaultUser,
  UserProvider,
} from '../context/UserContext';
// libs
import { onAuthStateChange, refreshAuthSessionState } from '../utils/firebase';
// components
import Breweries from '../pages/breweries/Breweries';
import NhStyleFingerprintProfilePage from '../pages/nhStyleFingerprints/NhStyleFingerprintProfilePage';
import NhStyleFingerprintsPage from '../pages/nhStyleFingerprints/NhStyleFingerprintsPage';
import NhStyleFingerprintShell from '../pages/nhStyleFingerprints/NhStyleFingerprintShell';
import Dashboard from '../pages/dashboard/Dashboard';
import Users from '../pages/users/Users';
import Login from '../pages/login/Login';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import AuthActionPage from '../pages/AuthActionPage';
import AuthEmailConfirmedPage from '../pages/AuthEmailConfirmedPage';
import LogoForm from '../pages/brewery/overrideBrewery/logoForm/LogoForm';
import Brewery from '../pages/brewery/Brewery';
import EditUserForm from '../pages/users/EditUserForm';
import PasswordResetPage from '../pages/PasswordResetPage';
import CrawlerFailuresPage from '../pages/failures/CrawlerFailuresPage';
import DescriptorRequestsPage from '../pages/descriptorRequests/DescriptorRequestsPage';
import CrawlerFailureDetailsPage from '../pages/failures/CrawlerFailureDetailsPage';
import Splats from '../pages/splats/Splats';
import MergeBreweries from '../pages/breweries/MergeBreweries';
import BreweryGroups from '../pages/breweries/BreweryGroups';
import BeerStyles from '../pages/beerStyles/BeerStyles';
import BeerList from '../pages/beer/BeerList';
import BeerDuplicateReviewPage from '../pages/beer/BeerDuplicateReviewPage';
import Checkins from '../pages/checkins/Checkins';
import Reviews from '../pages/reviews/Reviews';
import UserAddedLocations from '../pages/userAddedLocations/UserAddedLocations';
import Metrics from '../pages/metrics/Metrics';
import Events from '../pages/events/Events';
import EventDetailPage from '../pages/events/EventDetailPage';
import CrawlerSchedulerPage from '../pages/crawlerScheduler/CrawlerSchedulerPage';
import BeerIngestionPage from '../pages/beerIngestion/BeerIngestionPage';
import BeerIngestionReviewPage from '../pages/beerIngestion/BeerIngestionReviewPage';

import BreweryClaimPage from '../pages/breweryPortal/BreweryClaimPage';
import BreweryClaimStatusPage from '../pages/breweryPortal/BreweryClaimStatusPage';
import BrewhouseDashboardPage from '../pages/breweryPortal/BrewhouseDashboardPage';
import ReviewDetailsPage from '../pages/breweryPortal/ReviewDetailsPage';
import PintPortfolioPage from '../pages/breweryPortal/PintPortfolioPage';
import PintPortfolioBeerProfilePage from '../pages/breweryPortal/PintPortfolioBeerProfilePage';
import PintPortfolioLayout from '../components/pintPortfolio/PintPortfolioLayout';
import ClaimedBreweriesPage from '../pages/breweryClaims/ClaimedBreweriesPage';
import BreweryActivityEmailsPage from '../pages/emailNotifications/BreweryActivityEmailsPage';
import NewsletterCampaignsPage from '../pages/newsletterCampaigns/NewsletterCampaignsPage';
import NewsletterCampaignDetailPage from '../pages/newsletterCampaigns/NewsletterCampaignDetailPage';
import BreweryEmailPreferencesPage from '../pages/breweryPortal/BreweryEmailPreferencesPage';
import EmailUnsubscribePage from '../pages/EmailUnsubscribePage';
import BrewhousePortalLayout from '../components/layout/BrewhousePortalLayout';

// import AddBrewery from '../pages/brewery/editbrewery/AddBrewery';
//
import BreweryAdminPortalRoute from './BreweryAdminPortalRoute';
import BreweryPortalRoute from './BreweryPortalRoute';
import BrewhousePortalLandingRedirect from './BrewhousePortalLandingRedirect';
import GlobalAdminRoute from './GlobalAdminRoute';
import RootRedirect from './RootRedirect';

function AppRoutes() {
  const [user, setUser] = useState(defaultUser);

  const refreshSession = useCallback(
    () => refreshAuthSessionState(setUser),
    [setUser],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChange(setUser);
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthSessionSyncContext.Provider value={refreshSession}>
      <UserProvider value={user}>
        <Router>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/home" element={<RootRedirect />} />
          <Route
            path="/dashboard"
            element={
              <GlobalAdminRoute>
                <Dashboard />
              </GlobalAdminRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/auth/email-confirmed" element={<AuthEmailConfirmedPage />} />
          <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />
          <Route
            path="/brewery-portal"
            element={
              <BreweryPortalRoute>
                <BrewhousePortalLayout />
              </BreweryPortalRoute>
            }
          >
            <Route index element={<BrewhousePortalLandingRedirect />} />
            <Route
              path="dashboard"
              element={(
                <BreweryAdminPortalRoute>
                  <BrewhouseDashboardPage />
                </BreweryAdminPortalRoute>
              )}
            />
            <Route
              path="email-preferences"
              element={(
                <BreweryAdminPortalRoute>
                  <BreweryEmailPreferencesPage />
                </BreweryAdminPortalRoute>
              )}
            />
            <Route
              path="review-details"
              element={(
                <BreweryAdminPortalRoute>
                  <ReviewDetailsPage />
                </BreweryAdminPortalRoute>
              )}
            />
            <Route
              path="pint-portfolio"
              element={(
                <BreweryAdminPortalRoute>
                  <PintPortfolioLayout />
                </BreweryAdminPortalRoute>
              )}
            >
              <Route index element={<PintPortfolioPage />} />
              <Route path=":beerId" element={<PintPortfolioBeerProfilePage />} />
            </Route>
            <Route path="claim" element={<BreweryClaimPage />} />
            <Route path="status" element={<BreweryClaimStatusPage />} />
          </Route>
          <Route
            path="/brewery-portal/manage"
            element={<BrewhousePortalLandingRedirect />}
          />
          <Route
            exact
            path="/breweries"
            element={
              <GlobalAdminRoute>
                <Breweries />
              </GlobalAdminRoute>
            }
          />
          <Route
            path="/nh-style-fingerprints"
            element={
              <GlobalAdminRoute>
                <NhStyleFingerprintShell />
              </GlobalAdminRoute>
            }
          >
            <Route index element={<NhStyleFingerprintsPage />} />
            <Route path=":identityId" element={<NhStyleFingerprintProfilePage />} />
          </Route>
          <Route
            exact
            path="/brewery-groups"
            element={
              <GlobalAdminRoute>
                <BreweryGroups />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/email-notifications"
            element={
              <GlobalAdminRoute>
                <BreweryActivityEmailsPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/newsletter-campaigns"
            element={
              <GlobalAdminRoute>
                <NewsletterCampaignsPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/newsletter-campaigns/:campaignId"
            element={
              <GlobalAdminRoute>
                <NewsletterCampaignDetailPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/claimed-breweries"
            element={
              <GlobalAdminRoute>
                <ClaimedBreweriesPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/brewery/:id"
            element={
              <GlobalAdminRoute>
                <Brewery />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/brewery/logo/edit/:id"
            element={
              <GlobalAdminRoute>
                <LogoForm />
              </GlobalAdminRoute>
            }
          />
          {/* <Route
            exact
            path="/brewery/add"
            element={
              <GlobalAdminRoute>
                <AddBrewery />
              </GlobalAdminRoute>
            }
          /> */}
          <Route
            exact
            path="/users"
            element={
              <GlobalAdminRoute>
                <Users />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/user/:id"
            element={
              <GlobalAdminRoute>
                <EditUserForm />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/descriptor-requests"
            element={
              <GlobalAdminRoute>
                <DescriptorRequestsPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/failures"
            element={
              <GlobalAdminRoute>
                <CrawlerFailuresPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/crawler-scheduler"
            element={
              <GlobalAdminRoute>
                <CrawlerSchedulerPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/beer-ingestion"
            element={
              <GlobalAdminRoute>
                <BeerIngestionPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/beer-ingestion/review/:runId"
            element={
              <GlobalAdminRoute>
                <BeerIngestionReviewPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/failures/:id"
            element={
              <GlobalAdminRoute>
                <CrawlerFailureDetailsPage />
              </GlobalAdminRoute>
            }
          />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route
            exact
            path="/splats"
            element={
              <GlobalAdminRoute>
                <Splats />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/metrics"
            element={
              <GlobalAdminRoute>
                <Metrics />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/events"
            element={
              <GlobalAdminRoute>
                <Events />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/events/:eventId"
            element={
              <GlobalAdminRoute>
                <EventDetailPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/beer-styles"
            element={
              <GlobalAdminRoute>
                <BeerStyles />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/beer/dupes/review/:reviewId"
            element={
              <GlobalAdminRoute>
                <BeerDuplicateReviewPage />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/beer"
            element={
              <GlobalAdminRoute>
                <BeerList />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/checkins"
            element={
              <GlobalAdminRoute>
                <Checkins />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/reviews"
            element={
              <GlobalAdminRoute>
                <Reviews />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/user-added-locations"
            element={
              <GlobalAdminRoute>
                <UserAddedLocations />
              </GlobalAdminRoute>
            }
          />
          <Route
            exact
            path="/breweries/merge/:idA/:idB"
            element={
              <GlobalAdminRoute>
                <MergeBreweries />
              </GlobalAdminRoute>
            }
          />
        </Routes>
        </Router>
      </UserProvider>
    </AuthSessionSyncContext.Provider>
  );
}

export { AppRoutes };
